import { useMount, useUnmount, useUpdateEffect } from 'ahooks';
import { useEffect } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';

import { AddressChangerSocketEventNames, useGetAddressChangerSocket } from '@/modules/AddressChanger';

export const useAddressChangerSocket = <
  Data
>(
    eventName: AddressChangerSocketEventNames,
    cb: (args: Omit<ReturnType<typeof useSocketEvent<Data>>, 'socket' | 'sendMessage'>) => void,
    events?: {
      onConnect?: (eventSocket: ReturnType<typeof useSocket>['socket']) => void;
      onDisconnect?: (eventSocket: ReturnType<typeof useSocket>['socket']) => void;
      onError?: (eventSocket: ReturnType<typeof useSocket>['socket']) => void;
      onMount?: (eventSocket: ReturnType<typeof useSocket>['socket']) => void;
      onUnmount?: (eventSocket: ReturnType<typeof useSocket>['socket']) => void;
    }
  ): void => {
  const { onConnect, onDisconnect, onError, onUnmount, onMount } = events || {};

  const { socket } = useGetAddressChangerSocket();

  const {
    error,
    lastMessage,
    status,
    socket: socketInstance,
  } = useSocketEvent(socket, eventName);

  useUpdateEffect(() => {
    if (lastMessage && cb) {
      cb({ error, lastMessage: lastMessage as Data, status });
    }
  }, [lastMessage, eventName]);

  useEffect(() => {
    if (status === 'connected') {
      onConnect?.(socketInstance);
    }
    if (status === 'disconnected') {
      onDisconnect?.(socketInstance);
    }
  }, [status, socketInstance]);

  useUpdateEffect(() => {
    if (error) {
      onError?.(socketInstance);
    }
  }, [error, socketInstance]);

  useMount(() => {
    onMount?.(socketInstance);
  });

  useUnmount(() => {
    onUnmount?.(socketInstance);
  });
};
